import { useState } from 'react';
import { Check } from '../../types';
import { statusLabels } from '../../utils/status';
import { formatDateTime } from '../../utils/datetime';

interface Props {
  checks: Check[];
  count?: number;
  /** Draw a tick at the right edge marking "now". */
  showNow?: boolean;
}

const statusFill: Record<string, string> = {
  up: 'bg-status-up',
  degraded: 'bg-status-degraded',
  down: 'bg-status-down',
};

const EMPTY = 'bg-gray-200 dark:bg-gray-700';

export function UptimeBar({ checks, count = 30, showNow = false }: Props) {
  // `checks` arrives newest-first. Reverse to oldest-first, then pad on the
  // LEFT so the most recent check always sits at the right edge — otherwise a
  // service with only a few checks renders its history followed by a long grey
  // tail, which reads as "stopped reporting" rather than "recently added".
  const recent = checks.slice(0, count).reverse();
  const cells: (Check | null)[] = [
    ...Array<Check | null>(Math.max(0, count - recent.length)).fill(null),
    ...recent,
  ];

  const [hovered, setHovered] = useState<number | null>(null);

  const summary = recent.length
    ? `Last ${recent.length} check${recent.length > 1 ? 's' : ''}: ` +
      `${recent.filter(c => c.status === 'up').length} up, ` +
      `${recent.filter(c => c.status === 'degraded').length} degraded, ` +
      `${recent.filter(c => c.status === 'down').length} down`
    : 'No checks recorded yet';

  const active = hovered !== null ? cells[hovered] : null;

  return (
    <div className="relative" onMouseLeave={() => setHovered(null)}>
      {/* Screen readers get the summary; 40 focusable segments would be worse
          than useless for keyboard users, so the tooltip is pointer-only. */}
      <div className="flex items-center gap-[2px] h-6" role="img" aria-label={summary}>
        {cells.map((check, i) => (
          <div
            key={i}
            onMouseEnter={() => setHovered(i)}
            className={`flex-1 h-full rounded-sm transition-[transform,background-color] ${
              check ? statusFill[check.status] ?? EMPTY : EMPTY
            } ${hovered === i ? 'scale-y-125' : ''}`}
          />
        ))}
        {showNow && (
          <span
            aria-hidden="true"
            className="ml-[3px] w-[2px] self-stretch shrink-0 rounded-none bg-gray-800 dark:bg-gray-200"
          />
        )}
      </div>

      {/* Replaces the native title= tooltip: no 1–2s delay, styled with the
          rest of the app, and it shows a readable local time. */}
      {active && (
        <div
          className="pointer-events-none absolute z-20 bottom-full mb-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 shadow-overlay"
          role="presentation"
        >
          <span className="font-medium">{statusLabels[active.status]}</span>
          {active.response_time !== null && (
            <span className="text-gray-300 tabular-nums"> · {active.response_time}ms</span>
          )}
          <span className="block text-gray-400 tabular-nums">{formatDateTime(active.timestamp)}</span>
        </div>
      )}
    </div>
  );
}
